import { useIntl } from 'react-intl';

import { DescriptionList } from '~/eds/src/components/DescriptionList/DescriptionList';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';

import { INTAKE_FORM_SECTION_ID } from '../../constants';
import { useDisplayQuestionValue } from '../WorkflowDisplayForm/useDisplayQuestionValue';
import { sortAndFilterWellKnownFields } from './utils';
import { WellKnownFieldsFormInformation } from './WellKnownFieldsFormInformation';

interface Props {
  ticketId: string;
  isEditing?: boolean;
  normalizedWellKnownFields?: Record<string, any>;
  search?: string;
}

export const useWellKnownFieldAccordionSection = ({
  ticketId,
  search,
}: Props): {
  content: JSX.Element;
  hash: string;
  headerClass: string;
  isExpanded: boolean;
  title: string;
} | null => {
  const intl = useIntl();
  const WSSDesignatedFeatureFlag = useFlag(FlagType.WSSDesignatedWorkflow);
  const isIntakeFormEDSComponents = useFlag(FlagType.IntakeFormEDSComponents);
  const isWSSIntegrationPhase2M1M3Enabled = useFlag(
    FlagType.WSSIntegrationPhase2M1M3,
  );
  const displayQuestionValue = useDisplayQuestionValue();

  if (isWSSIntegrationPhase2M1M3Enabled) {
    return null;
  }

  const {
    data: wellKnownFieldsFromApi,
    isLoading,
    error,
  } = api.endpoints.getTicketWellKnownFields.useQuery(ticketId, {
    skip: !WSSDesignatedFeatureFlag,
  });

  if (
    !WSSDesignatedFeatureFlag ||
    isLoading ||
    (!error &&
      (!wellKnownFieldsFromApi ||
        Object.values(wellKnownFieldsFromApi.fields).length === 0))
  ) {
    return null;
  }

  const content = isIntakeFormEDSComponents ? (
    <DescriptionList
      terms={
        wellKnownFieldsFromApi
          ? sortAndFilterWellKnownFields(wellKnownFieldsFromApi.fields).map(
              (field) => ({
                title: field.name,
                description: displayQuestionValue({
                  ...field,
                  value: { value: field.value },
                }),
              }),
            )
          : []
      }
      search={search}
    />
  ) : wellKnownFieldsFromApi ? (
    <WellKnownFieldsFormInformation
      wellKnownFields={wellKnownFieldsFromApi}
      search={search}
    />
  ) : null;

  return {
    hash: 'well-known-fields',
    headerClass: INTAKE_FORM_SECTION_ID,
    isExpanded: true,
    title: intl.formatMessage({
      id: 'UseWellKnownFieldAccordionSection.strategicSourcingTitle',
      defaultMessage: 'Sourcing Information',
      description:
        'Title for the strategic sourcing accordion section on the intake form',
    }),
    content: error ? (
      <StatusMessage
        status="danger"
        message={intl.formatMessage({
          id: 'UseWellKnownFieldAccordionSection.loadingErrorMessage',
          defaultMessage: 'Error loading Sourcing Information',
          description:
            'Error message when strategic sourcing data fails to load on the intake form',
        })}
      />
    ) : (
      (content ?? <></>)
    ),
  };
};
